/**
 * Google Translate Debug Service
 * Helps identify why Google Translate is not working
 */

class GoogleTranslateDebugger {
    private static instance: GoogleTranslateDebugger;
    
    private constructor() {
        this.startDebugging();
    }
    
    static getInstance(): GoogleTranslateDebugger {
        if (!GoogleTranslateDebugger.instance) {
            GoogleTranslateDebugger.instance = new GoogleTranslateDebugger();
        }
        return GoogleTranslateDebugger.instance;
    }
    
    private startDebugging(): void {
        
        // Check for Google Translate elements
        this.checkGoogleTranslateElements();
        
        // Monitor network requests
        this.monitorNetworkRequests();
        
        // Check cookies
        this.checkCookies();
        
        // Monitor DOM for Google Translate
        this.monitorDOM();
        
        // Add manual trigger function
        this.addManualTrigger();
    }
    
    private checkGoogleTranslateElements(): void {
        
        const checks = {
            script: document.getElementById('google-translate-script'),
            container: document.getElementById('google_translate_element'),
            select: document.querySelector('.goog-te-combo'),
            widget: document.querySelector('.goog-te-gadget'),
            googleAPI: (window as any).google?.translate,
            translateElement: (window as any).google?.translate?.TranslateElement,
            initFunction: (window as any).googleTranslateElementInit
        };
        
        
        // Check every 2 seconds
        setInterval(() => {
            const select = document.querySelector('.goog-te-combo') as HTMLSelectElement;
            if (select) {
                console.warn('Google Translate select found! Value:', select.value);
            }
        }, 2000);
    }
    
    private monitorNetworkRequests(): void {
        // Intercept fetch to see if Google Translate API is being called
        const originalFetch = window.fetch;
        (window as any).fetch = function(...args: any[]) {
            return originalFetch.apply(window, args as [RequestInfo | URL, RequestInit?]);
        };
        
        // Monitor XHR
        const originalOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function(method: string, url: string, ...args: any[]) {
            return originalOpen.apply(this, [method, url, ...args] as any);
        };
    }
    
    private checkCookies(): void {
        const cookies = document.cookie.split(';');
        const googleTransCookie = cookies.find(c => c.includes('googtrans'));
        console.warn('🍪 Google Translate cookie:', googleTransCookie || 'Not found');
    }
    
    private monitorDOM(): void {
        // Watch for Google Translate DOM changes
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1) {
                            const element = node as Element;
                            // className can be SVGAnimatedString on SVG elements, not a string
                            const classStr = typeof element.className === 'string'
                                ? element.className
                                : (element as SVGElement).className?.baseVal ?? '';
                            const idStr = typeof element.id === 'string' ? element.id : '';
                            if (classStr.includes('goog-te') ||
                                idStr.includes('goog') ||
                                classStr.includes('translated')) {
                                console.warn('🔄 Google Translate DOM element added:', element);
                            }
                        }
                    });
                }
                
                if (mutation.type === 'attributes' && mutation.target === document.documentElement) {
                    if (mutation.attributeName === 'class') {
                        const classes = document.documentElement.className;
                        if (classes.includes('translated')) {
                            console.warn('📝 Page translated! Classes:', classes);
                        }
                    }
                }
            });
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });
    }
    
    private addManualTrigger(): void {
        // Add functions to window for manual testing
        (window as any).debugGoogleTranslate = {
            // Manually trigger translation
            triggerTranslation: (lang: string = 'es') => {
                
                // Set cookie
                document.cookie = `googtrans=/en/${lang};path=/`;
                
                // Try to find and trigger select
                const select = document.querySelector('.goog-te-combo') as HTMLSelectElement;
                if (select) {
                    select.value = lang;
                    select.dispatchEvent(new Event('change', { bubbles: true }));
                } else {
                    console.error('Google Translate select not found!');
                }
                
            },
            
            // Force initialize
            forceInit: () => {
                
                // Check if script exists
                let script = document.getElementById('google-translate-script') as HTMLScriptElement;
                if (!script) {
                    const newScript = document.createElement('script') as HTMLScriptElement;
                    newScript.id = 'google-translate-script';
                    newScript.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
                    document.head.appendChild(newScript);
                    script = newScript;
                }
                
                // Ensure init function exists
                (window as any).googleTranslateElementInit = () => {
                    const google = (window as any).google;
                    if (google?.translate?.TranslateElement) {
                        // Ensure container exists
                        let container = document.getElementById('google_translate_element');
                        if (!container) {
                            container = document.createElement('div');
                            container.id = 'google_translate_element';
                            container.style.display = 'none';
                            document.body.appendChild(container);
                        }
                        
                        container.innerHTML = '';
                        
                        new google.translate.TranslateElement(
                            {
                                pageLanguage: 'en',
                                includedLanguages: 'en,es',
                                autoDisplay: false
                            },
                            'google_translate_element'
                        );
                    } else {
                        console.error('Google Translate API not available');
                    }
                };
                
                // If Google API already loaded, call init directly
                if ((window as any).google?.translate) {
                    (window as any).googleTranslateElementInit();
                }
            },
            
            // Check current status
            status: () => {
                return {
                    script: !!document.getElementById('google-translate-script'),
                    container: !!document.getElementById('google_translate_element'),
                    select: !!document.querySelector('.goog-te-combo'),
                    googleAPI: !!(window as any).google?.translate,
                    cookie: document.cookie.includes('googtrans'),
                    translated: document.documentElement.classList.contains('translated-ltr') || 
                               document.documentElement.classList.contains('translated-rtl'),
                    savedLanguage: localStorage.getItem('selectedLanguage')
                };
            }
        };
        
    }
}

// Initialize debugger instance
const debuggerInstance = GoogleTranslateDebugger.getInstance();

export default debuggerInstance;